<template>
  <el-dialog
    :title="$t('user_profile.your_profile.modals.edit_profile.title')"
    :visible="dialogVisible"
    :before-close="handleClose"
    :close-on-press-escape=true
    custom-class="elsa-modal"
    :center=false
    width="255"
    top="50px"
    v-on:closed="closed"
  >
    <api-errors :errors="errors" />
    <el-form :model="form" :rules="rules" ref="editProfileForm" @change.native="onEditProfileChange">
      <upload class="upload-image">
        <template v-slot:label>
          {{ $t('user_profile.your_profile.modals.edit_profile.upload_image.label') }}
        </template>
      </upload>

      <elsa-input
        name="first_name"
        :label="$t('user_profile.your_profile.modals.edit_profile.first_name.label')"
        :placeholder="$t('user_profile.your_profile.modals.edit_profile.first_name.placeholder')"
        v-model.lazy="form.first_name"
      >

      </elsa-input>

      <elsa-input
        name="last_name"
        :label="$t('user_profile.your_profile.modals.edit_profile.last_name.label')"
        :placeholder="$t('user_profile.your_profile.modals.edit_profile.last_name.placeholder')"
        v-model.lazy="form.last_name"
      >

      </elsa-input>

      <elsa-input
        name="email"
        :label="$t('user_profile.your_profile.modals.edit_profile.email.label')"
        :placeholder="$t('user_profile.your_profile.modals.edit_profile.email.placeholder')"
        v-model.trim.lazy="form.email"
      >

      </elsa-input>

      <elsa-input
        name="username"
        :label="$t('user_profile.your_profile.modals.edit_profile.user_name.label')"
        :placeholder="$t('user_profile.your_profile.modals.edit_profile.user_name.placeholder')"
        v-model.lazy="form.username"
      >
      </elsa-input>

      <div class="phone-wrapper">
        <country-select
          :label="$t('user_profile.your_profile.modals.edit_profile.phone.label')"
          :placeholder="$t('user_profile.your_profile.modals.edit_profile.phone.code_placeholder')"
          v-model="form.phone_code"
          type="phone"
          name="phone_code"
        />
        <elsa-input
          type="number"
          name="phone_number"
          :placeholder="$t('user_profile.your_profile.modals.edit_profile.phone.placeholder')"
          v-model.lazy="form.phone_number"
        >
          {{ $t('user_profile.your_profile.modals.edit_profile.phone.label') }}
        </elsa-input>
      </div>

    </el-form>

    <span slot="footer" class="dialog-footer">
      <primary-button ref="handleUpdateUserDataButton" @click.native="handleUpdateUserDataClick">{{$t('user_profile.your_profile.modals.edit_profile.save')}}</primary-button>
      <tertiary-button @click.native="handleCancelClick">{{$t('user_profile.your_profile.modals.edit_profile.cancel')}}</tertiary-button>
    </span>
  </el-dialog>
</template>

<script>
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'
  import TertiaryButton from '@/components/common/buttons/TertiaryButton'
  import Upload from '@/components/common/Upload'
  import ElsaInput from '@/components/common/Input'
  import ApiErrors from '@/components/common/ApiErrors'
  import CountrySelect from '@/components/common/CountrySelect'
  import {
    juxt,
    map,
    reduce,
    or,
    compose,
    head,
    split,
    pathOr, last, reject, either, isNil, isEmpty, when, trim, always } from 'ramda'
  import { isPropEmpty, handleSubmitButtonState } from '../../../helpers/formValidationHelper'
  import { successMessage } from '@/utils/messages'

  export default {
    name: "EditProfile",
    props: ["dialogVisible", "userData"],
    components: {
      PrimaryButton,
      Upload,
      TertiaryButton,
      ElsaInput,
      ApiErrors,
      CountrySelect,
    },
    data() {
      return {
        errors: [],
        form: {},
        rules: {
          first_name: { required: true, type: 'string', message: this.$t('user_profile.your_profile.modals.edit_profile.first_name.validation_msg'), trigger: 'blur' },
          last_name: { required: true, type: 'string', message: this.$t('user_profile.your_profile.modals.edit_profile.last_name.validation_msg'), trigger: 'blur' },
          email: { required: true, type: 'email', message: this.$t('user_profile.your_profile.modals.edit_profile.email.validation_msg'), trigger: 'blur' }
        }
      }
    },
    methods: {
      onEditProfileChange() {
        const preds = map(isPropEmpty)(['first_name', 'last_name', 'email'])
        const predValues = juxt(preds)(this.form)
        const isInvalid = reduce(or, false, predValues)

        return handleSubmitButtonState(this.$refs.handleUpdateUserDataButton.$el, !isInvalid)
      },
      handleUpdateUserDataClick() {
        const phoneNumber = when(
          compose(
            isEmpty,
            trim,
          ),
          always(null)
        )(`${this.form.phone_code} ${this.form.phone_number}`)

        const userData = {
          ...this.userData,
          ...this.form,
          phone_number: phoneNumber,
        }

        const cleanUserData = reject(either(isNil, isEmpty),  userData)

        this.$refs.editProfileForm.validate((valid) => {
          const showConfirmNewEmailModal = this.userData.email !== this.form.email;
          if(valid) {
            return this.$store.dispatch('user/updateUserData', cleanUserData)
              .then(() => {
                this.$emit('closed')
                if(showConfirmNewEmailModal) {
                  this.$emit('emailChanged', true)
                }

                return successMessage(this.$t('user_profile.your_profile.modals.edit_profile.update_success'))(this)
              })
              .catch(this.handleError)
          }
        })
      },
      handleCancelClick () { this.$emit("closed") },
      handleClose () { this.$emit("closed") },
      closed () { this.form = {} },
    },
    watch: {
      dialogVisible (visible) {
        if(visible) {

          const phoneCode = compose(
            head,
            split(' '),
            pathOr('', ['userData', 'phone_number'])
          )(this)

          const phoneNumber = compose(
            last,
            split(' '),
            pathOr('', ['userData', 'phone_number'])
          )(this)

          this.form = {
            ...this.userData,
            phone_code: phoneCode,
            phone_number: phoneNumber,
          }
        }
        else { this.form = { } }
      }
    }
  }
</script>

<style lang="scss" scoped>
form {
  & > div:first-child {
    display: flex;
    justify-content: center;
    margin: 0 auto;
  }
  .el-form-item {
    margin-bottom: 20px;
  }
}

.show-confirm-new-email {
  display: flex;
  flex-direction: column;

  h2 {
    font-weight: 500;
    font-size: 18px;
    color: #546873;
    opacity: 1;
  }

  p {
    text-align: left
  }
}
</style>
